import React from "react";
import { Box, Skeleton } from "@mui/material";
import { COLORS } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";

/**
 * Bitişik seçenek grubu (tek seçim) — Gün/Hafta, Tümü/Tamamlanan/İptal,
 * dönem seçimi gibi 2–6 seçenekli geçişler için tek görünüm.
 * Görünüm, panel dönem seçicisinin (PeriodSelector variant="segmented") yerleşik
 * tasarımıdır: aktif seçenek tema rengiyle dolu.
 *
 * @param {string} value
 * @param {(value: string) => void} onChange
 * @param {Array<{value: string, label: React.ReactNode}>} options
 * @param {string} [ariaLabel] - grup için erişilebilir ad
 * @param {boolean} [fullWidth] - seçenekler genişliği eşit paylaşır
 * @param {boolean} [isLoading] - iskelet gösterir
 * @param {object} [palette] - verilmezse klinik teması kullanılır
 */
export default function SegmentedControl({
  value,
  onChange,
  options = [],
  ariaLabel,
  fullWidth = false,
  isLoading = false,
  palette: paletteProp,
  sx = {},
}) {
  const { palette: themePalette } = useClinicTheme();
  const palette = paletteProp || themePalette;

  const wrapperSx = {
    display: fullWidth ? "flex" : "inline-flex",
    alignItems: "center",
    gap: 0.5,
    p: 0.5,
    borderRadius: 3,
    bgcolor: COLORS.white,
    border: `1px solid ${COLORS.slate200}`,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    flexWrap: fullWidth ? "nowrap" : "wrap",
    ...sx,
  };

  if (isLoading) {
    return (
      <Box sx={wrapperSx}>
        {options.map((o) => (
          <Skeleton key={o.value} variant="rounded" width={56} height={30} sx={{ borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box role="radiogroup" aria-label={ariaLabel} sx={wrapperSx}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <Box
            key={o.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => {
              if (!active) onChange?.(o.value);
            }}
            sx={{
              flex: fullWidth ? 1 : "0 0 auto",
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              border: "none",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
              cursor: active ? "default" : "pointer",
              bgcolor: active ? palette?.primary || COLORS.slate800 : "transparent",
              color: active ? palette?.contrastText || COLORS.white : COLORS.slate600,
              boxShadow: active ? "0 1px 2px rgba(15, 23, 42, 0.12)" : "none",
              transition: "background-color 200ms ease, color 200ms ease",
              "&:hover": active ? {} : { bgcolor: COLORS.slate100, color: COLORS.slate800 },
              "&:focus-visible": { outline: `2px solid ${palette?.primary || COLORS.slate800}`, outlineOffset: 1 },
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
}
